import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Bell, X, Check, CheckCheck, Trash2, Package, Truck, FileText, Users, DollarSign, Wrench, AlertTriangle, MessageSquare, CheckCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { 
  getUserNotifications, 
  getUnreadCount, 
  markAsRead, 
  markAllAsRead, 
  deleteNotification,
  subscribeToNotifications,
  type Notification 
} from '../../services/notificationService';
import { useToast } from '../../contexts/ToastContext';

export function NotificationDropdown() {
  const { profile } = useAuth();
  const toast = useToast();
  const [showDropdown, setShowDropdown] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (profile?.id) {
      loadNotifications();
      loadUnreadCount();

      // Subscribe to real-time notifications
      const unsubscribe = subscribeToNotifications(profile.id, async (newNotification) => {
        setNotifications(prev => [newNotification, ...prev]);
        setUnreadCount(prev => prev + 1);
        
        // Check quiet hours before showing toast (respects user's browser timezone)
        const shouldShowToast = await checkQuietHours(profile.id, newNotification.priority);
        if (shouldShowToast) {
          toast.info(newNotification.title);
        }
      });

      return () => {
        unsubscribe();
      };
    }
  }, [profile]);

  const checkQuietHours = async (userId: string, priority: string) => {
    // Always show urgent/critical notifications
    if (priority === 'urgent') {
      return true;
    }

    try {
      const { data } = await supabase
        .from('notification_preferences')
        .select('quiet_hours_enabled, quiet_hours_start, quiet_hours_end')
        .eq('user_id', userId)
        .single();

      if (!data || !data.quiet_hours_enabled) {
        return true; // Quiet hours not enabled, show notification
      }

      // Get current time in user's browser timezone
      const now = new Date();
      const currentTime = now.toTimeString().slice(0, 5); // HH:MM format

      const startTime = data.quiet_hours_start?.substring(0, 5) || '22:00';
      const endTime = data.quiet_hours_end?.substring(0, 5) || '07:00';

      // Check if current time is within quiet hours
      if (startTime < endTime) {
        // Normal case: e.g., 22:00 to 07:00 (crosses midnight)
        return currentTime < startTime || currentTime >= endTime;
      } else {
        // Reverse case: e.g., 08:00 to 20:00 (doesn't cross midnight)
        return currentTime >= endTime && currentTime < startTime;
      }
    } catch (error) {
      console.error('Error checking quiet hours:', error);
      return true; // On error, show notification
    }
  };

  const loadNotifications = async () => {
    if (!profile?.id) return;
    
    setLoading(true);
    const result = await getUserNotifications(profile.id);
    if (result.success) {
      setNotifications(result.notifications);
    }
    setLoading(false);
  };

  const loadUnreadCount = async () => {
    if (!profile?.id) return;
    
    const result = await getUnreadCount(profile.id);
    if (result.success) {
      setUnreadCount(result.count);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    const result = await markAsRead(notificationId);
    if (result.success) {
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!profile?.id) return;
    
    const result = await markAllAsRead(profile.id);
    if (result.success) {
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    }
  };

  const handleDelete = async (notificationId: string) => {
    const result = await deleteNotification(notificationId);
    if (result.success) {
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      const notification = notifications.find(n => n.id === notificationId);
      if (notification && !notification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      handleMarkAsRead(notification.id);
    }
    if (notification.link) {
      // Fix old notification links that use /dispatch/ instead of /dispatches/
      const fixedLink = notification.link.replace(/\/dispatch\//, '/dispatches/');
      window.location.href = fixedLink;
      setShowDropdown(false);
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'new_load': return Package;
      case 'new_dispatch': return Truck;
      case 'dispatch_status_change': return Truck;
      case 'driver_sms_message': return MessageSquare;
      case 'new_driver_added': return Users;
      case 'new_truck_added': return Truck;
      case 'new_trailer_added': return Truck;
      case 'document_uploaded': return FileText;
      case 'maintenance_due': return Wrench;
      case 'maintenance_completed': return CheckCircle;
      case 'inspection_due': return AlertTriangle;
      case 'accident_reported': return AlertTriangle;
      case 'load_ready_billing': return DollarSign;
      case 'invoice_created': return DollarSign;
      case 'payment_received': return DollarSign;
      case 'driver_available': return Users;
      case 'driver_unavailable': return Users;
      default: return Bell;
    }
  };

  const getIconColor = (type: string) => {
    switch (type) {
      case 'new_load': return 'bg-blue-600';
      case 'new_dispatch': return 'bg-green-600';
      case 'dispatch_status_change': return 'bg-teal-600';
      case 'driver_sms_message': return 'bg-purple-600';
      case 'new_driver_added': return 'bg-indigo-600';
      case 'new_truck_added': return 'bg-slate-600';
      case 'new_trailer_added': return 'bg-slate-700';
      case 'document_uploaded': return 'bg-purple-600';
      case 'maintenance_due': return 'bg-orange-600';
      case 'maintenance_completed': return 'bg-green-600';
      case 'inspection_due': return 'bg-yellow-600';
      case 'accident_reported': return 'bg-red-600';
      case 'load_ready_billing': return 'bg-emerald-600';
      case 'invoice_created': return 'bg-green-600';
      case 'payment_received': return 'bg-green-700';
      case 'driver_available': return 'bg-green-600';
      case 'driver_unavailable': return 'bg-slate-600';
      default: return 'bg-slate-600';
    }
  };

  const getPriorityBorder = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'border-l-4 border-red-600';
      case 'high': return 'border-l-4 border-orange-600';
      case 'normal': return 'border-l-4 border-blue-600';
      case 'low': return 'border-l-4 border-slate-600';
      default: return '';
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 hover:bg-slate-800 rounded-lg transition-colors"
      >
        <Bell className="w-5 h-5 text-slate-400" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex items-center justify-center">
            <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping"></span>
            <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500 text-white text-[10px] font-bold items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          </span>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && createPortal(
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0"
            style={{ zIndex: '2147483646' }}
            onClick={() => setShowDropdown(false)}
          ></div>

          {/* Dropdown Content */}
          <div className="fixed right-4 top-16 w-96 bg-slate-800 border border-slate-700 rounded-lg shadow-xl max-h-[600px] flex flex-col" style={{ zIndex: '2147483647' }}>
            {/* Header */}
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-white">Notifications</h3>
                {unreadCount > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-red-500 text-white font-semibold">
                    {unreadCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                  >
                    <CheckCheck className="w-3 h-3" />
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => setShowDropdown(false)}
                  className="text-slate-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="text-slate-400 text-sm mt-2">Loading...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm">No notifications yet</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-700">
                  {notifications.map((notification) => {
                    const Icon = getIcon(notification.type);
                    const iconColor = getIconColor(notification.type);
                    const priorityBorder = getPriorityBorder(notification.priority);

                    return (
                      <div
                        key={notification.id}
                        className={`p-4 hover:bg-slate-750 transition-colors cursor-pointer ${
                          !notification.is_read ? 'bg-slate-700/50' : ''
                        } ${priorityBorder}`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg ${iconColor} flex items-center justify-center flex-shrink-0`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <h4 className={`text-sm font-semibold ${
                                notification.is_read ? 'text-slate-300' : 'text-white'
                              }`}>
                                {notification.title}
                              </h4>
                              {!notification.is_read && (
                                <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1"></span>
                              )}
                            </div>
                            <p className="text-xs text-slate-400 line-clamp-2 mb-2">
                              {notification.message}
                            </p>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-slate-500">
                                {formatTime(notification.created_at)}
                              </span>
                              <div className="flex items-center gap-1">
                                {!notification.is_read && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMarkAsRead(notification.id);
                                    }}
                                    className="p-1 hover:bg-slate-600 rounded transition-colors"
                                    title="Mark as read"
                                  >
                                    <Check className="w-3 h-3 text-slate-400" />
                                  </button>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(notification.id);
                                  }}
                                  className="p-1 hover:bg-slate-600 rounded transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="w-3 h-3 text-slate-400" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="p-3 border-t border-slate-700">
                <button
                  onClick={() => {
                    setShowDropdown(false);
                    setTimeout(() => {
                      window.location.href = '/notifications';
                    }, 100);
                  }}
                  className="w-full text-center text-sm text-blue-400 hover:text-blue-300 font-medium"
                >
                  View all notifications
                </button>
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
